import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Bot, User as UserIcon, Shield } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'randevu_chat_session';

function MessageBubble({ role, content }) {
  const isUser = role === 'user';
  const isAdmin = role === 'admin';
  const bg = isUser ? '#17b4b4' : isAdmin ? '#f59e0b' : '#f3f4f6';
  const color = isUser || isAdmin ? '#fff' : '#1a1a1a';
  const Icon = isUser ? UserIcon : isAdmin ? Shield : Bot;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      {!isUser && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6b7280', marginBottom: 3 }}>
          <Icon size={11} /> {isAdmin ? 'Admin' : 'AI köməkçisi'}
        </div>
      )}
      <div style={{ background: bg, color, padding: '8px 12px', borderRadius: 12, maxWidth: 240, fontSize: 13.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {content}
      </div>
    </div>
  );
}

export default function ChatBubble() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || '');
  const scrollRef = useRef(null);

  // Restore session on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!sessionId) return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/chat/session/${sessionId}`);
        const data = await res.json();
        if (alive && data?.messages) setMessages(data.messages);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  // Poll for new messages (admin replies) when chat is open
  useEffect(() => {
    if (!open || !sessionId) return;
    const int = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/chat/session/${sessionId}`);
        const data = await res.json();
        if (data?.messages && data.messages.length !== messages.length) {
          setMessages(data.messages);
        }
      } catch { /* noop */ }
    }, 5000);
    return () => clearInterval(int);
  }, [open, sessionId, messages.length]);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    if (!text.trim() || sending) return;
    const userMsg = { id: `tmp-${Date.now()}`, role: 'user', content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    const messageBody = { session_id: sessionId || null, message: text.trim() };
    setText('');
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/chat/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageBody),
      });
      const data = await res.json();
      if (data?.session_id) {
        if (!sessionId) {
          localStorage.setItem(SESSION_KEY, data.session_id);
          setSessionId(data.session_id);
        }
        setMessages((prev) => {
          const withoutTmp = prev.filter((m) => m.id !== userMsg.id);
          const next = [...withoutTmp, data.user_message];
          if (data.ai_message) next.push(data.ai_message);
          return next;
        });
      }
    } catch {
      setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: 'assistant', content: 'Xəta baxş verdi. Yenidən cəhd edin.' }]);
    } finally {
      setSending(false);
    }
  };

  const showWelcome = messages.length === 0;

  return (
    <>
      {open && (
        <div style={{
          position: 'fixed', bottom: 96, right: 24, width: 340, background: '#fff',
          borderRadius: 12, boxShadow: '0 20px 50px rgba(0,0,0,0.18)', overflow: 'hidden', zIndex: 40,
          border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', maxHeight: '70vh',
        }}>
          <div style={{ background: '#17b4b4', color: '#fff', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: 15 }}>{settings.chat_title}</strong>
            <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={18} /></button>
          </div>
          <div ref={scrollRef} style={{ padding: 14, minHeight: 200, maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, background: '#fafafa' }}>
            {showWelcome && (
              <MessageBubble role="assistant" content={settings.chat_welcome} />
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} role={m.role} content={m.content} />
            ))}
            {sending && (
              <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Bot size={11} /> Yazılır...
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #eee', background: '#fff' }}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Mesajınız..."
              disabled={sending}
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 999, outline: 'none', fontSize: 13 }}
            />
            <button onClick={send} disabled={sending} style={{ background: '#17b4b4', color: '#fff', border: 'none', width: 36, height: 36, borderRadius: '50%', cursor: sending ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
      <div className="chat-bubble" onClick={() => setOpen(!open)}>
        <MessageCircle size={26} />
      </div>
    </>
  );
}
